import { z } from 'zod';
import { isEmbeddableUrl } from '../replicated-sections';
import { uuidSchema } from './common';

/**
 * LMS — course / chapter / lesson authoring + member progress schemas. Shared
 * by the API routes (validation) and the admin / creator SPAs (form typing).
 */

export const COURSE_ACCESS_LEVELS = ['free', 'purchase', 'subscriber'] as const;
export type CourseAccessLevel = (typeof COURSE_ACCESS_LEVELS)[number];

export const COURSE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export type CourseLevel = (typeof COURSE_LEVELS)[number];

export const COURSE_STATUSES = ['draft', 'published'] as const;

export const LESSON_SOURCE_TYPES = ['embed', 'r2', 'article'] as const;
export type LessonSourceType = (typeof LESSON_SOURCE_TYPES)[number];

const bulletList = z.array(z.string().min(1).max(300)).max(20);

/**
 * Content language code — lowercase BCP-47-ish ('en', 'fil', 'zh-hant').
 * Display + catalog filter only; there is no i18n framework.
 */
export const languageCodeSchema = z
  .string()
  .regex(/^[a-z]{2,3}(-[a-z0-9]{2,8})?$/i, 'Must be a language code like "en" or "zh-hant"')
  .max(16);

/** Create a course on top of an existing VIRTUAL product. */
export const courseCreateSchema = z.object({
  productId: uuidSchema,
  accessLevel: z.enum(COURSE_ACCESS_LEVELS).optional(),
  /** Course topic — the SEPARATE course-category registry (not products.categories). */
  categoryId: uuidSchema.nullable().optional(),
  subtitle: z.string().max(300).nullable().optional(),
  learnPoints: bulletList.optional(),
  requirements: bulletList.optional(),
  level: z.enum(COURSE_LEVELS).nullable().optional(),
  status: z.enum(COURSE_STATUSES).optional(),
  language: languageCodeSchema.nullable().optional(),
  /** Replace-set: the whole tag list, not a delta. */
  tagIds: z.array(uuidSchema).max(10).optional(),
});
export type CourseCreateInput = z.infer<typeof courseCreateSchema>;

export const courseUpdateSchema = courseCreateSchema.omit({ productId: true }).partial();
export type CourseUpdateInput = z.infer<typeof courseUpdateSchema>;

/**
 * `?force=true` on a chapter / lesson DELETE. A plain `z.coerce.boolean()`
 * would read the string 'false' as TRUE, which is exactly the wrong default
 * for a destructive flag — so the literal enum is deliberate.
 */
export const courseDeleteQuerySchema = z.object({
  force: z.enum(['true', 'false']).optional(),
});
export type CourseDeleteQuery = z.infer<typeof courseDeleteQuerySchema>;

export const chapterUpsertSchema = z.object({
  title: z.string().min(1).max(200),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type ChapterUpsertInput = z.infer<typeof chapterUpsertSchema>;

/**
 * Lesson authoring. `embedUrl` is only meaningful for `sourceType='embed'`;
 * an R2 lesson gets its video through the presign/finalize pair, never here.
 * `body` is only meaningful for `sourceType='article'`.
 */
export const lessonUpsertSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  sourceType: z.enum(LESSON_SOURCE_TYPES).optional(),
  // Scheme-checked, not just URL-shaped: a lesson URL is author-supplied by a
  // self-registered CREATOR and lands in an iframe src inside the member
  // portal, so `javascript:` / `data:` would execute in our origin. The refine
  // sits on the string (not the object) so the creator PATCH's `.partial()`
  // keeps it.
  embedUrl: z
    .string()
    .url()
    .max(2000)
    .refine(isEmbeddableUrl, { message: 'Must be an http(s) URL' })
    .nullable()
    .optional(),
  durationSeconds: z
    .number()
    .int()
    .min(0)
    .max(24 * 3600)
    .nullable()
    .optional(),
  isPreview: z.boolean().optional(),
  /**
   * Article content — HTML from the creator's rich-text editor,
   * `sourceType='article'` only. Sanitized SERVER-SIDE by `sanitizeLessonBody`
   * (the max here bounds the INPUT, before sanitizing).
   */
  body: z.string().max(100_000).nullable().optional(),
  /** Drip: days after the learner's enrollment before this unlocks. */
  availableAfterDays: z.number().int().min(0).max(3650).nullable().optional(),
  /** Drip: absolute unlock timestamp (ISO). Both set = the later one wins. */
  availableFrom: z.string().datetime().nullable().optional(),
});
export type LessonUpsertInput = z.infer<typeof lessonUpsertSchema>;

/** Bulk reorder: ids in the desired order. */
export const reorderSchema = z.object({ ids: z.array(uuidSchema).min(1).max(500) });

/** Member: mark a lesson complete. `via` = how the completion was detected. */
export const lessonCompleteSchema = z.object({
  via: z.enum(['player', 'manual']).default('manual'),
});

/**
 * Member: the playback heartbeat body — where the learner is in the lesson.
 * Capped at 24 h so a broken player (or a hostile client) can't store an
 * absurd resume pointer; the column is a plain integer of SECONDS.
 */
export const lessonPositionSchema = z.object({
  positionSeconds: z
    .number()
    .int()
    .min(0)
    .max(24 * 3600),
  /**
   * The furthest second WATCHED, which the server keeps as a monotonic maximum.
   * Optional so an older client (or the mobile shell mid-rollout) keeps working
   * — the server falls back to `positionSeconds`, which can only understate it.
   */
  maxPositionSeconds: z
    .number()
    .int()
    .min(0)
    .max(24 * 3600)
    .optional(),
  /** The learner reached the end. Unlocks free seeking on this lesson. */
  ended: z.boolean().optional(),
});
export type LessonPositionInput = z.infer<typeof lessonPositionSchema>;

/**
 * Member: `POST .../open` body. `refresh:true` is a re-presign of an already
 * open lesson (the TTL on a signed R2 URL ran out mid-playback) — it must NOT
 * count as a new open, so the route skips every stat and side effect.
 */
export const lessonOpenSchema = z.object({ refresh: z.boolean().optional() });
export type LessonOpenInput = z.infer<typeof lessonOpenSchema>;

/** Creator/admin: request a presigned PUT for an R2 lesson video. */
export const videoPresignSchema = z.object({
  contentLength: z.number().int().min(1),
});
/**
 * Creator/admin: confirm the upload landed; the API sniffs + sizes it.
 *
 * `durationSeconds` is the browser's own probe of the file it just uploaded
 * (`<video preload="metadata">` on an object URL) — the author no longer has
 * to type a runtime the file already knows. OPTIONAL: a probe that fails or
 * times out simply omits it, and the service then leaves the column alone
 * rather than NULLing a value an author may have typed by hand.
 */
export const videoFinalizeSchema = z.object({
  key: z.string().min(1).max(512),
  durationSeconds: z
    .number()
    .int()
    .min(1)
    .max(24 * 3600)
    .optional(),
});

/**
 * Multipart upload (Phase 9). A 1 GB course video over a Philippine
 * residential or mobile link is not one PUT — any hiccup restarts it from zero.
 * These four schemas carry a chunked upload that can retry a single part,
 * resume after a page reload and be cancelled outright.
 */
export const videoMultipartStartSchema = z.object({
  contentLength: z.number().int().min(1),
});

/**
 * Re-sign parts whose URLs expired mid-upload (they last 6 hours) or that a
 * resumed upload still needs. Capped so one call cannot ask for thousands of
 * signatures; a client with more parts pages through.
 */
export const videoMultipartSignSchema = z.object({
  key: z.string().min(1).max(512),
  uploadId: z.string().min(1).max(512),
  partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(200),
});

export const videoMultipartCompleteSchema = z.object({
  key: z.string().min(1).max(512),
  uploadId: z.string().min(1).max(512),
  /** Same optional-probe semantics as `videoFinalizeSchema`. */
  durationSeconds: z
    .number()
    .int()
    .min(1)
    .max(24 * 3600)
    .optional(),
});

export const videoMultipartAbortSchema = z.object({
  key: z.string().min(1).max(512),
  uploadId: z.string().min(1).max(512),
});

// ── Certificates ────────────────────────────────────────────────────────────
export const certificatePlaceholderSchema = z.object({
  field: z.enum(['member_name', 'course_title', 'issued_date', 'verify_code']),
  xPct: z.number().min(0).max(100),
  yPct: z.number().min(0).max(100),
  widthPct: z.number().min(1).max(100),
  fontSizePx: z.number().int().min(8).max(200),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  align: z.enum(['left', 'center', 'right']),
});
export const certificateTemplateConfigSchema = z.object({
  placeholders: z.array(certificatePlaceholderSchema).max(8),
});
export type CertificatePlaceholder = z.infer<typeof certificatePlaceholderSchema>;
export type CertificateTemplateConfigInput = z.infer<typeof certificateTemplateConfigSchema>;
/** Alias for the config shape; the two names are used interchangeably in the SPAs. */
export type CertificateTemplateConfig = CertificateTemplateConfigInput;

export const certificateTemplateUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  config: certificateTemplateConfigSchema.optional(),
});
export const courseCertificateAssignSchema = z.object({
  templateId: uuidSchema.nullable(),
});

// ── Tests (quiz gate before the certificate) ────────────────────────────────
export const TEST_QUESTION_KINDS = ['multiple_choice', 'essay'] as const;
export type TestQuestionKind = (typeof TEST_QUESTION_KINDS)[number];

/**
 * Create/update a test. `chapterId` IS the placement: a uuid = quiz after
 * that chapter, null/absent on create = the course's final test. Placement
 * is create-time only (move = delete + recreate; keeps attempt history
 * unambiguous), so update omits it.
 */
export const testUpsertSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  chapterId: uuidSchema.nullable().optional(),
  passingScorePct: z.number().int().min(1).max(100).optional(),
  maxAttempts: z.number().int().min(1).max(100).nullable().optional(),
  active: z.boolean().optional(),
  /**
   * Practice test: MC only, unlimited retakes, answers revealed after submit,
   * never a certificate gate. CREATE-TIME ONLY, exactly like `chapterId` — a
   * test that already carries attempts must not change meaning under them,
   * which is also what keeps the graded-only unique indexes honest. `update`
   * omits it, so a PATCH that names it is a silent no-op.
   */
  isPractice: z.boolean().optional(),
});
export type TestUpsertInput = z.infer<typeof testUpsertSchema>;
export const testUpdateSchema = testUpsertSchema
  .omit({ chapterId: true, isPractice: true })
  .partial();
export type TestUpdateInput = z.infer<typeof testUpdateSchema>;

export const testQuestionUpsertSchema = z
  .object({
    kind: z.enum(TEST_QUESTION_KINDS).default('multiple_choice'),
    prompt: z.string().min(1).max(2000),
    choices: z.array(z.string().min(1).max(500)).max(10).optional(),
    correctIndex: z.number().int().min(0).max(9).nullable().optional(),
    /**
     * Why the answer is right. Stored on any question, but only ever SHOWN to
     * a learner on a practice attempt — on a graded test it is as server-only
     * as `correctIndex`.
     */
    explanation: z.string().max(2000).nullable().optional(),
    points: z.number().int().min(1).max(100).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .superRefine((q, ctx) => {
    if (q.kind === 'multiple_choice') {
      const choices = q.choices ?? [];
      if (choices.length < 2)
        ctx.addIssue({
          code: 'custom',
          message: 'multiple_choice_needs_choices',
          path: ['choices'],
        });
      if (q.correctIndex == null || q.correctIndex >= choices.length)
        ctx.addIssue({ code: 'custom', message: 'invalid_correct_index', path: ['correctIndex'] });
    } else if ((q.choices?.length ?? 0) > 0 || q.correctIndex != null) {
      ctx.addIssue({ code: 'custom', message: 'essay_has_no_choices', path: ['choices'] });
    }
  });
export type TestQuestionUpsertInput = z.infer<typeof testQuestionUpsertSchema>;

/** Member: submit an attempt — one answer per question. */
export const testAttemptSubmitSchema = z.object({
  answers: z
    .array(
      z.object({
        questionId: uuidSchema,
        selectedIndex: z.number().int().min(0).max(9).nullable().optional(),
        text: z.string().max(8000).nullable().optional(),
      }),
    )
    .min(1)
    .max(200),
});
export type TestAttemptSubmitInput = z.infer<typeof testAttemptSubmitSchema>;

/** Creator/admin: grade a pending attempt's essay questions. */
export const attemptGradeSchema = z.object({
  grades: z
    .array(
      z.object({
        questionId: uuidSchema,
        points: z.number().int().min(0).max(100),
        comment: z.string().max(2000).nullable().optional(),
      }),
    )
    .max(200),
});
export type AttemptGradeInput = z.infer<typeof attemptGradeSchema>;

// ── Q&A ─────────────────────────────────────────────────────────────────────
export const questionCreateSchema = z.object({ body: z.string().min(1).max(2000) });
export const questionHideSchema = z.object({ hidden: z.boolean() });

// ── Learner notes / bookmarks ───────────────────────────────────────────────
/**
 * Create a note. An EMPTY body is a BOOKMARK (the `B` key drops a bare
 * timestamp), which is why `body` defaults to `''` instead of being required.
 * `positionSeconds` is nullable: a note about the lesson as a whole has no
 * timestamp.
 */
export const noteCreateSchema = z.object({
  positionSeconds: z
    .number()
    .int()
    .min(0)
    .max(24 * 3600)
    .nullable()
    .optional(),
  body: z.string().trim().max(4000).default(''),
});
export type NoteCreateInput = z.infer<typeof noteCreateSchema>;

/** Edit a note's text (a bookmark becomes a note by gaining a body). */
export const noteUpdateSchema = z.object({ body: z.string().trim().max(4000) });
export type NoteUpdateInput = z.infer<typeof noteUpdateSchema>;

/**
 * List filters. No `lessonId` = the whole course ("All lessons"). The limit
 * ALWAYS applies — a learner's notes are private and few, but an unbounded
 * list is still an unbounded query.
 */
export const notesListQuerySchema = z.object({
  lessonId: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type NotesListQuery = z.infer<typeof notesListQuerySchema>;

/**
 * Admin: course TAG registry — the many-to-many taxonomy beside categories.
 * Declared FIRST and standalone: categories gained `parentId` (one level of
 * nesting) and tags are flat, so aliasing one to the other would have silently
 * accepted a parent on a tag.
 */
export const courseTagUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  active: z.boolean().optional(),
});

/**
 * Admin: course category (topic) registry — separate from product categories.
 * `parentId` nests ONE level: a root may have children, a child may not. The
 * depth rule lives in `services/courses.ts`; this schema only carries the id.
 */
export const courseCategoryUpsertSchema = courseTagUpsertSchema.extend({
  parentId: uuidSchema.nullable().optional(),
});

// ── Lesson attachments + captions ───────────────────────────────────────────
/** Multipart text fields alongside an attachment upload. */
export const lessonFileUploadSchema = z.object({
  title: z.string().min(1).max(200),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
/** Multipart text fields alongside a WebVTT caption upload. */
export const lessonCaptionUploadSchema = z.object({
  lang: languageCodeSchema,
  label: z.string().min(1).max(80),
});

// ── Admin enrollment grant / revoke ─────────────────────────────────────────
/**
 * Manually grant course access. The recovery tool for a sale rung on the
 * wrong account — `expiresAt` null (or absent) grants forever.
 */
export const enrollmentGrantSchema = z.object({
  userId: uuidSchema,
  expiresAt: z.string().datetime().nullable().optional(),
});
export type EnrollmentGrantInput = z.infer<typeof enrollmentGrantSchema>;

// ── Storefront catalog query ────────────────────────────────────────────────
/**
 * Server-side course catalog filters. `limit` ALWAYS applies (the query was
 * an unbounded full scan with aggregate joins); the generous default keeps
 * today's SPA — which pages nothing — rendering whole catalogs.
 */
export const COURSE_SORTS = [
  'featured',
  'newest',
  'popular',
  'rating',
  'price_asc',
  'price_desc',
] as const;
export type CourseSort = (typeof COURSE_SORTS)[number];

export const courseCatalogQuerySchema = z.object({
  search: z.string().max(120).optional(),
  categoryId: uuidSchema.optional(),
  tagId: uuidSchema.optional(),
  level: z.enum(COURSE_LEVELS).optional(),
  accessLevel: z.enum(COURSE_ACCESS_LEVELS).optional(),
  language: languageCodeSchema.optional(),
  sort: z.enum(COURSE_SORTS).optional(),
  /**
   * Drop courses the CALLER is already enrolled in. A string flag, not a
   * boolean: this rides a querystring, and the user id comes from the JWT —
   * never from the client — so an anonymous request simply ignores it.
   */
  excludeOwned: z.enum(['1']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

/**
 * A course announcement — a creator broadcasting to that course's enrolled
 * students. `body` is rich text, sanitized SERVER-side by
 * `createCourseAnnouncement` / `updateCourseAnnouncement` (never in the route),
 * so the same limit covers markup + text.
 */
export const courseAnnouncementCreateSchema = z.object({
  title: z.string().trim().min(1).max(200),
  body: z.string().min(1).max(20_000),
  /** Publish (and ring every enrolled learner's bell) immediately. */
  publish: z.boolean().optional(),
});
export const courseAnnouncementUpdateSchema = courseAnnouncementCreateSchema
  .omit({ publish: true })
  .partial();
export type CourseAnnouncementCreateInput = z.infer<typeof courseAnnouncementCreateSchema>;

/**
 * Phase 8 — link this course to a sibling that is the SAME course in another
 * language (the Udemy model: one course per language, grouped). Catalog
 * metadata only: it never touches the product, so it never re-opens review.
 */
export const courseTranslationLinkSchema = z.object({
  courseId: uuidSchema,
});
